import { useMutationWithToast } from "@/hooks/shared/use-mutation-with-toast";
import { api } from "@/lib/api-typed";
import type {
  ContentBlockCreate,
  TrainingModuleCreate,
  TrainingLessonCreate,
} from "@/types";

// ========== Module Mutations ==========

export const useCreateModule = () => {
  return useMutationWithToast({
    mutationFn: async (data: { training_id: string; module: TrainingModuleCreate }) => {
      return api.post("/trainings/{training_id}/modules", { path: { training_id: data.training_id }, body: data.module });
    },
    invalidateQueries: (_, variables) => [
      ["training-modules", variables.training_id],
      ["trainings", variables.training_id],
    ],
  });
};

export const useUpdateModule = () => {
  return useMutationWithToast({
    mutationFn: async (data: {
      module_id: string;
      title?: string;
      description?: string;
      sort_order?: number;
      is_required?: boolean;
    }) => {
      const { module_id, ...updateData } = data;
      return api.patch("/trainings/modules/{module_id}", { path: { module_id }, body: updateData });
    },
    invalidateQueries: [
      ["training-modules"],
      ["trainings"],
    ],
  });
};

export const useDeleteModule = () => {
  return useMutationWithToast({
    mutationFn: async (moduleId: string) => {
      return api.delete("/trainings/modules/{module_id}", { path: { module_id: moduleId } });
    },
    invalidateQueries: [
      ["training-modules"],
      ["trainings"],
    ],
  });
};

// ========== Lesson Mutations ==========

export const useCreateLesson = () => {
  return useMutationWithToast({
    mutationFn: async (data: { module_id: string; lesson: TrainingLessonCreate }) => {
      return api.post("/trainings/modules/{module_id}/lessons", { path: { module_id: data.module_id }, body: data.lesson });
    },
    invalidateQueries: (_, variables) => [
      ["training-lessons", variables.module_id],
      ["training-modules"],
    ],
  });
};

export const useUpdateLesson = () => {
  return useMutationWithToast({
    mutationFn: async (data: {
      lesson_id: string;
      title?: string;
      description?: string;
      sort_order?: number;
      estimated_duration_minutes?: number;
    }) => {
      const { lesson_id, ...updateData } = data;
      return api.patch("/trainings/lessons/{lesson_id}", { path: { lesson_id }, body: updateData });
    },
    invalidateQueries: [
      ["training-lessons"],
      ["training-modules"],
    ],
  });
};

export const useDeleteLesson = () => {
  return useMutationWithToast({
    mutationFn: async (lessonId: string) => {
      return api.delete("/trainings/lessons/{lesson_id}", { path: { lesson_id: lessonId } });
    },
    invalidateQueries: [
      ["training-lessons"],
      ["training-modules"],
    ],
  });
};

export const useCreateLessonContentBlock = () => {
  return useMutationWithToast({
    mutationFn: async (data: { lesson_id: string; block: ContentBlockCreate }) => {
      return api.post("/trainings/lessons/{lesson_id}/blocks", { path: { lesson_id: data.lesson_id }, body: data.block });
    },
    invalidateQueries: (_, variables) => [
      ["content-blocks", "lesson", variables.lesson_id],
    ],
  });
};

// ========== Progress Mutations ==========

export const useUpdateTrainingProgress = () => {
  return useMutationWithToast({
    mutationFn: async (data: {
      training_id: string;
      current_module_id?: string | null;
      current_lesson_id?: string | null;
      last_content_block_id?: string | null;
      time_spent_seconds?: number;
    }) => {
      const { training_id, ...updateData } = data;
      return api.post("/trainings/{training_id}/progress", { path: { training_id }, body: updateData });
    },
    invalidateQueries: (_, variables) => [
      ["training-progress", variables.training_id],
      ["trainings", "my-trainings"],
    ],
  });
};

export const useMarkLessonComplete = () => {
  return useMutationWithToast({
    mutationFn: async (data: { lesson_id: string; time_spent_seconds: number }) => {
      const timeSpent = Math.max(1, Math.round(data.time_spent_seconds));
      return api.post("/trainings/lessons/{lesson_id}/complete", { path: { lesson_id: data.lesson_id }, query: { time_spent: timeSpent } });
    },
    invalidateQueries: [
      // Invalidate all training-progress keys so live lesson completions + percentage update
      ["training-progress"],
      ["training-lessons"],
      ["trainings", "my-trainings"],
      // Also invalidate module-completions so Statistics tab updates
      ["training-module-completions"],
    ],
  });
};
